import { LIVE_CHANGE_EVENT, LIVE_HEARTBEAT_EVENT, LIVE_HEARTBEAT_MS, LIVE_READY_EVENT, LiveEvent } from '@rp/contracts'

export const LIVE_EVENTS_PATH = '/v1/events'
export const LIVE_RECONNECT_DELAY_MS = 5_000
export const LIVE_MAX_RECONNECT_DELAY_MS = 60_000
export const LIVE_MISSED_HEARTBEATS = 2
export const LIVE_STALL_SLACK_MS = 10_000
export const LIVE_STALL_MS = LIVE_HEARTBEAT_MS * LIVE_MISSED_HEARTBEATS + LIVE_STALL_SLACK_MS
export const LIVE_FIRST_HEARTBEAT_GRACE_FACTOR = 5
export const LIVE_FIRST_HEARTBEAT_GRACE_MS = LIVE_STALL_MS * LIVE_FIRST_HEARTBEAT_GRACE_FACTOR
export const LIVE_DOWN_GRACE_MS = 10_000

const EVENT_SOURCE_CLOSED = 2

export interface LiveEventSource {
  readonly readyState: number
  addEventListener(type: string, listener: (event: Event) => void): void
  close(): void
}

export type LiveEventSourceFactory = (url: string) => LiveEventSource | null

export interface LiveSubscriptionOptions {
  baseUrl: string
  tenantId: string
  onEvent: (event: LiveEvent) => void
  onReady: () => void
  onClosed?: () => void
  onConnection?: (connected: boolean) => void
  onRefused?: () => void
  factory?: LiveEventSourceFactory
}

const browserEventSource: LiveEventSourceFactory = (url) =>
  typeof EventSource === 'undefined' ? null : new EventSource(url)

export const liveEventsUrl = (baseUrl: string, tenantId: string): string => {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  return `${base}${LIVE_EVENTS_PATH}?${new URLSearchParams({ tenant: tenantId }).toString()}`
}

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const liveEventOf = (event: Event): LiveEvent | null => {
  if (!(event instanceof MessageEvent) || typeof event.data !== 'string') return null
  const parsed = LiveEvent.safeParse(parseJson(event.data))
  return parsed.success ? parsed.data : null
}

type Timer = ReturnType<typeof setTimeout> | undefined

interface ConnectionWatch {
  down: () => void
  downNow: () => void
  up: () => void
  stop: () => void
}

const watchConnection = (report: (connected: boolean) => void): ConnectionWatch => {
  let grace: Timer
  let reported = false
  const declare = () => {
    clearTimeout(grace)
    grace = undefined
    if (reported) return
    reported = true
    report(false)
  }
  return {
    down: () => {
      if (grace === undefined && !reported) grace = setTimeout(declare, LIVE_DOWN_GRACE_MS)
    },
    downNow: declare,
    up: () => {
      clearTimeout(grace)
      grace = undefined
      if (!reported) return
      reported = false
      report(true)
    },
    stop: () => clearTimeout(grace),
  }
}

export const subscribeLiveEvents = (options: LiveSubscriptionOptions): (() => void) => {
  const factory = options.factory ?? browserEventSource
  const url = liveEventsUrl(options.baseUrl, options.tenantId)
  const connection = watchConnection((connected) => options.onConnection?.(connected))
  let source: LiveEventSource | null = null
  let reopen: Timer
  let stall: Timer
  let stopped = false
  let attempt = 0

  const nextDelayMs = (): number => {
    const delay = Math.min(LIVE_RECONNECT_DELAY_MS * 2 ** attempt, LIVE_MAX_RECONNECT_DELAY_MS)
    attempt += 1
    return delay
  }

  const scheduleReopen = () => {
    clearTimeout(reopen)
    reopen = setTimeout(open, nextDelayMs())
  }

  const onStalled = () => {
    source?.close()
    connection.downNow()
    options.onClosed?.()
    scheduleReopen()
  }

  const watchFor = (ms: number) => {
    clearTimeout(stall)
    stall = setTimeout(onStalled, ms)
  }

  const onError = (opened: LiveEventSource) => {
    if (stopped || opened !== source) return
    options.onClosed?.()
    connection.down()
    if (opened.readyState !== EVENT_SOURCE_CLOSED) return
    clearTimeout(stall)
    options.onRefused?.()
    scheduleReopen()
  }

  const listen = (opened: LiveEventSource) => {
    let beating = false
    const current = (handler: (event: Event) => void) => (event: Event) => {
      if (opened === source) handler(event)
    }
    const alive = () => watchFor(beating ? LIVE_STALL_MS : LIVE_FIRST_HEARTBEAT_GRACE_MS)
    opened.addEventListener(LIVE_READY_EVENT, current(() => {
      attempt = 0
      alive()
      connection.up()
      options.onReady()
    }))
    opened.addEventListener(LIVE_HEARTBEAT_EVENT, current(() => {
      beating = true
      alive()
    }))
    opened.addEventListener(LIVE_CHANGE_EVENT, current((event) => {
      alive()
      const change = liveEventOf(event)
      if (change !== null) options.onEvent(change)
    }))
    opened.addEventListener('error', () => onError(opened))
  }

  function open(): void {
    const opened = factory(url)
    source = opened
    if (opened === null) return
    listen(opened)
    watchFor(LIVE_STALL_MS)
  }

  open()

  return () => {
    stopped = true
    clearTimeout(reopen)
    clearTimeout(stall)
    connection.stop()
    source?.close()
  }
}
